import { useEffect, useMemo, type FocusEvent } from 'react';
import { create } from 'zustand';

/** How the person is in an area: the pointer over it, or focus inside it. */
type Presence = 'pointer' | 'focus';

interface InteractionHoldState {
  /** A card is being dragged (`BoardCanvas`'s `activeTask`). */
  dragging: boolean;
  /** Card menus open now; a count, so one closing late cannot clear another. */
  menusOpen: number;
  /** The column (a step id) or `'list'` the pointer is over. */
  pointerZone: string | null;
  /** The column or `'list'` keyboard focus is inside. */
  focusZone: string | null;
  setDragging: (dragging: boolean) => void;
  menuOpened: () => void;
  menuClosed: () => void;
  enter: (presence: Presence, zone: string) => void;
  leave: (presence: Presence, zone: string) => void;
}

/**
 * What the person is in the middle of (spec 148 FR-002): the wire between the
 * places an interaction happens — the canvas's drag, a card's menu, a column
 * or the list under the pointer or holding focus — and the pages that hold
 * arrivals back while it lasts (`useArrivalHold`). The board also pauses its
 * idle refresh while a card is in the air (spec 126 FR-006).
 */
export const useInteractionHold = create<InteractionHoldState>((set) => ({
  dragging: false,
  menusOpen: 0,
  pointerZone: null,
  focusZone: null,
  setDragging: (dragging) => {
    set({ dragging });
  },
  menuOpened: () => {
    set((state) => ({ menusOpen: state.menusOpen + 1 }));
  },
  menuClosed: () => {
    set((state) => ({ menusOpen: Math.max(0, state.menusOpen - 1) }));
  },
  enter: (presence, zone) => {
    set(presence === 'pointer' ? { pointerZone: zone } : { focusZone: zone });
  },
  // Only the zone being left is cleared: entering the next column can be
  // reported before leaving this one.
  leave: (presence, zone) => {
    set((state) => {
      if (presence === 'pointer')
        return state.pointerZone === zone ? { pointerZone: null } : state;
      return state.focusZone === zone ? { focusZone: null } : state;
    });
  },
}));

/**
 * Makes an element a zone arrivals wait outside of while the pointer is over
 * it or focus is within it. Spread the result onto the element. A zone that
 * unmounts with the pointer still over it — a filter took the column away —
 * stops holding.
 */
export function useHoldZone(zone: string) {
  useEffect(
    () => () => {
      const { leave } = useInteractionHold.getState();
      leave('pointer', zone);
      leave('focus', zone);
    },
    [zone],
  );
  return useMemo(
    () => ({
      onPointerEnter: () => {
        useInteractionHold.getState().enter('pointer', zone);
      },
      onPointerLeave: () => {
        useInteractionHold.getState().leave('pointer', zone);
      },
      onFocus: () => {
        useInteractionHold.getState().enter('focus', zone);
      },
      // Focus moving between two cards of the same column stays inside it.
      onBlur: (event: FocusEvent<HTMLElement>) => {
        if (event.currentTarget.contains(event.relatedTarget)) return;
        useInteractionHold.getState().leave('focus', zone);
      },
    }),
    [zone],
  );
}

/**
 * Holds arrivals while `open` — a card's menu. Released on unmount too, so a
 * menu removed while open (its card deleted) cannot hold the page for good.
 */
export function useHoldWhileOpen(open: boolean) {
  useEffect(() => {
    if (!open) return;
    useInteractionHold.getState().menuOpened();
    return () => {
      useInteractionHold.getState().menuClosed();
    };
  }, [open]);
}
